/**
 * Ryan O'Dowd
 * 2024-02-17
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ScrollView,
  View,
} from 'react-native';
import Widgets from './Widgets';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

// @TODO: add analytics to track how many times each widget is opened
const MainDashboard = (props: MainDashboardProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);

  // @TODO: don't show all of these on first open...for example, wrinkle refresh shouldn't be visible until at least 24 hours after the first wrinkle is earned
  // @TODO: these should be able to have a badge for "new" (maybe keep up until timestamp...like 3 weeks)
  // @TODO: letting users configure their own dashboard is v2 (or later...)

  const lifetimePeanuts = hooks.useTypedSelector((state) => state.oakUser.lifetime_peanuts);
  const activeGoalId = hooks.useTypedSelector((state) => state.settingsActiveGoalId);
  const numGoals = hooks.useTypedSelector((state) => Object.keys(state.goals).length);
  const [_walkthroughVisible, setWalkthroughVisible] = useState(false);
  const tooltipShown = useRef(false);

  useEffect(() => {
    if (!tooltipShown.current && lifetimePeanuts < 1 && numGoals === 1 && activeGoalId === 20320) { // @TODO: don't hardcode wrinkly101
      setWalkthroughVisible(true);
      tooltipShown.current = true;
    }
  }, [lifetimePeanuts, activeGoalId, numGoals]);

  let numCols = 3;
  const scaledWidth = Math.min(globals.maxInnerWidth, styles.windowWidth) / styles.globalScale;
  // @TODO: test with different ipads too
  if (scaledWidth < 220) {
    numCols = 1;
  } else if (scaledWidth < 393) { // @TODO: iphone 15
    numCols = 2;
  } else if (scaledWidth >= globals.maxInnerWidth) {
    numCols = 5;
  } else if (scaledWidth > 500) {
    numCols = 4;
  }
  const cellSize = Math.min(globals.maxInnerWidth, styles.windowWidth) / numCols; // @TODO: do this next (windowwidth issue on desktop??)

  const layoutConfigsByColumn: any = { // @TODO: shouldn't just be about number of columns...e.g. 1 column looks different on small screen vs. bigger screen with larger scale because internal items have different sizes
    dailyGoals: {
      1: {x: 0, y: 0, w: 1, h: 1},
      2: {x: 0, y: 0, w: 1, h: 1},
      3: {x: 0, y: 0, w: 1, h: 1},
      4: {x: 0, y: 0, w: 1, h: 1},
      5: {x: 0, y: 0, w: 1, h: 1},
    },
    passageOfTheDay: {
      1: {x: 0, y: 1, w: 1, h: 1},
      2: {x: 0, y: 2, w: 2, h: 1},
      3: {x: 1, y: 0, w: 2, h: 1.75},
      4: {x: 1, y: 0, w: 2, h: 1},
      5: {x: 1, y: 0, w: 2, h: 2},
    },
    recentGoals: {
      1: {x: 0, y: 2, w: 1, h: 1},
      2: {x: 0, y: 1, w: 2, h: 1},
      3: {x: 1, y: 1.75, w: 2, h: 1.25},
      4: {x: 0, y: 1, w: 3, h: 1},
      5: {x: 3, y: 0, w: 2, h: 1},
    },
    wrinkleRefresh: {
      1: {x: 0, y: 3, w: 1, h: 1},
      2: {x: 0, y: 3, w: 2, h: 1},
      3: {x: 0, y: 3, w: 1.5, h: 1.5},
      4: {x: 0, y: 2, w: 2, h: 1},
      5: {x: 1, y: 2, w: 2, h: 2},
    },
    stormForecast: {
      1: {x: 0, y: 4, w: 1, h: 1},
      2: {x: 0, y: 4, w: 1, h: 1},
      3: {x: 1.5, y: 3, w: 1.5, h: 1},
      4: {x: 2, y: 2, w: 2, h: 1},
      5: {x: 3, y: 3, w: 2, h: 1},
    },
    friends: {
      1: {x: 0, y: 6, w: 1, h: 1},
      2: {x: 0, y: 5, w: 1, h: 1},
      3: {x: 1.5, y: 4, w: 1.5, h: 2},
      4: {x: 1, y: 3, w: 2, h: 1},
      5: {x: 3, y: 1, w: 2, h: 2},
    },
    continueReading: {
      1: {x: 0, y: 5, w: 1, h: 1},
      2: {x: 1, y: 0, w: 1, h: 1},
      3: {x: 0, y: 1, w: 1, h: 2},
      4: {x: 3, y: 0, w: 1, h: 2},
      5: {x: 0, y: 1, w: 1, h: 2},
    },
    help: {
      1: {x: 0, y: 7, w: 1, h: 1},
      2: {x: 1, y: 4, w: 1, h: 1},
      3: {x: 0, y: 4.5, w: 1.5, h: 1.5},
      4: {x: 0, y: 3, w: 1, h: 1},
      5: {x: 0, y: 3, w: 1, h: 1},
    },
  };

  let listHeight = 0;
  Object.values(layoutConfigsByColumn).forEach((config) => {
    listHeight = Math.max(listHeight, ((config[numCols].y + 1) * cellSize) + ((config[numCols].h - 1) * cellSize));
  });

  return (
    <View
      style={[
        styles.container,
        !props.visible && styles.hidden, // @NOTE: this is hidden instead of conditionally rendered because it's an expensive component
      ]}
    >
      <ScrollView
        contentContainerStyle={[styles.mainListContainer, {height: Math.max(listHeight, styles.windowHeight - 150) + styles.safeAreaInsets.bottom}]}// @TODO: shouldn't have to hardcode the padding // @TODO: shouldn't have to hardcode 150 for when bricks don't cover whole screen
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled={true}
      >
        {/* @TODO: the spacing on the right is less than on the left if columns is 2 (3 looks okay) */}
        <Widgets.DailyGoals
          cellSize={cellSize}
          layout={layoutConfigsByColumn.dailyGoals[numCols]}
        />

        <Widgets.PassageOfTheDay
          cellSize={cellSize}
          layout={layoutConfigsByColumn.passageOfTheDay[numCols]}
        />

        <Widgets.RecentGoals
          cellSize={cellSize}
          layout={layoutConfigsByColumn.recentGoals[numCols]}
          recentGoals={props.recentGoals}
          walkthroughProps={{
            isVisible: _walkthroughVisible,
            onClose: () => setWalkthroughVisible(false),
            title: i18n('startHere'),
            body: i18n('tutorialText'),
            placement: 'top',
          }}
        />

        <Widgets.WrinkleRefresh
          cellSize={cellSize}
          layout={layoutConfigsByColumn.wrinkleRefresh[numCols]}
        />

        <Widgets.StormForecast
          cellSize={cellSize}
          layout={layoutConfigsByColumn.stormForecast[numCols]}
        />

        <Widgets.Friends
          cellSize={cellSize}
          layout={layoutConfigsByColumn.friends[numCols]}
        />

        <Widgets.Help
          cellSize={cellSize}
          layout={layoutConfigsByColumn.help[numCols]}
        />

        <Widgets.ContinueReading
          cellSize={cellSize}
          layout={layoutConfigsByColumn.continueReading[numCols]}
        />
      </ScrollView>
    </View>
  );
};

class MainDashboardProps {
  recentGoals: any[];
  visible: boolean;
};

export default MainDashboard;
